import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sparkles,
  Download,
  Printer,
  Smartphone,
  FileText,
  RefreshCw,
  Package,
  Camera,
  Check,
  Image,
  X,
  Loader2,
  Clipboard,
  Trash2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price) =>
  new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" }).format(price || 0);

export const NewArrivalsPromoter = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [viewMode, setViewMode] = useState("select"); // select | whatsapp | a4
  const [headline, setHeadline] = useState("NOW IN STOCK!");
  const [subheadline, setSubheadline] = useState("New items just arrived at Bosveld Bou & Hardware");
  const [exporting, setExporting] = useState(false);
  const [showPrices, setShowPrices] = useState(true);
  const promoRef = useRef(null);
  const a4Ref = useRef(null);
  const fileInputRef = useRef(null);

  // Image upload state
  const [uploadProduct, setUploadProduct] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pastedImage, setPastedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Add product search state
  const [addSearch, setAddSearch] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);

  const HEADLINES = [
    "NOW IN STOCK!",
    "NEW ARRIVALS!",
    "JUST LANDED!",
    "FRESH STOCK!",
    "CHECK THIS OUT!",
    "AVAILABLE NOW!",
  ];

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/new-arrivals?days=${days}&limit=2000`);
      setProducts(res.data.products || []);
      // Auto-select all
      setSelected((res.data.products || []).map((p) => p.id));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load new arrivals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [days]);

  // Search any product to add manually
  const handleAddSearch = async (query) => {
    setAddSearch(query);
    if (query.length < 2) { setAddResults([]); return; }
    setAddSearching(true);
    try {
      const res = await axios.get(`${API}/products/search?q=${encodeURIComponent(query)}&limit=10`);
      const items = res.data.products || res.data || [];
      // Filter out products already in the list
      const existingIds = new Set(products.map(p => p.id));
      setAddResults(items.filter(p => !existingIds.has(p.id)));
    } catch {
      setAddResults([]);
    } finally {
      setAddSearching(false);
    }
  };

  const addProductToList = (product) => {
    const newProduct = {
      id: product.id,
      plu_code: product.plu_code,
      name: product.name,
      selling_price_incl_vat: product.selling_price_incl_vat,
      department: product.department,
      category: product.category,
      current_stock: product.current_stock,
      image_url: product.image_url,
      supplier: product.supplier,
    };
    setProducts(prev => [...prev, newProduct]);
    setSelected(prev => [...prev, product.id]);
    setAddResults(prev => prev.filter(p => p.id !== product.id));
    toast.success(`Added ${product.name}`);
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectedProducts = products.filter((p) => selected.includes(p.id));

  // Image upload handlers
  const openImageUpload = (product, e) => {
    e.stopPropagation();
    setUploadProduct(product);
    setPastedImage(null);
    setUploadDialogOpen(true);
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        const reader = new FileReader();
        reader.onload = (ev) => setPastedImage(ev.target.result);
        reader.readAsDataURL(blob);
        e.preventDefault();
        break;
      }
    }
  }, []);

  useEffect(() => {
    if (uploadDialogOpen) {
      document.addEventListener("paste", handlePaste);
      return () => document.removeEventListener("paste", handlePaste);
    }
  }, [uploadDialogOpen, handlePaste]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPastedImage(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPastedImage(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!pastedImage || !uploadProduct) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image_data", pastedImage);
      await axios.post(`${API}/product-images/upload/${uploadProduct.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Image saved for ${uploadProduct.name}`);
      setUploadDialogOpen(false);
      setPastedImage(null);
      fetchProducts();
    } catch (err) {
      console.error(err);
      // Surface the real backend error so Herman can see "Cloud storage
      // not configured" vs a 404 vs a network blip. Previously this was
      // a generic "Failed to upload image" that gave no clue what
      // actually went wrong — e.g. missing R2 env vars on production
      // after a republish just showed the same unhelpful toast.
      const detail = err.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Failed to upload image — ${detail}`, { duration: 10000 });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (product, e) => {
    e.stopPropagation();
    if (!confirm(`Remove image from ${product.name}?`)) return;
    try {
      await axios.delete(`${API}/product-images/${product.id}`);
      toast.success("Image removed");
      fetchProducts();
    } catch (err) {
      toast.error("Failed to remove image");
    }
  };

  const renderUploadDialog = () => (
    <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-blue-600" />
            Add Product Image
          </DialogTitle>
        </DialogHeader>

        {uploadProduct && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg flex items-center gap-3">
              <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                {uploadProduct.image_url ? (
                  <img src={uploadProduct.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Package className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">{uploadProduct.plu_code}</p>
                <p className="font-medium text-sm truncate">{uploadProduct.name}</p>
                <p className="text-sm text-amber-600 font-bold">{formatPrice(uploadProduct.selling_price_incl_vat)}</p>
              </div>
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
                pastedImage
                  ? "border-green-400 bg-green-50"
                  : dragOver
                  ? "border-blue-500 bg-blue-50 scale-[1.02]"
                  : "border-gray-300 hover:border-blue-400"
              }`}
              data-testid="image-drop-zone"
            >
              {pastedImage ? (
                <div className="space-y-3">
                  <img src={pastedImage} alt="Preview" className="max-h-48 mx-auto rounded-lg shadow" />
                  <Button variant="outline" size="sm" onClick={() => setPastedImage(null)}>
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                </div>
              ) : (
                <div className="text-gray-500">
                  <div className="flex justify-center gap-4 mb-3">
                    <Clipboard className="h-8 w-8 text-gray-400" />
                    <Upload className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="font-medium">Paste or Drop an image here</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Screenshot then <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Ctrl+V</kbd>, or drag a file
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-1" /> Browse Files
                  </Button>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleUploadImage}
                disabled={!pastedImage || uploading}
                className="bg-green-600 hover:bg-green-700"
                data-testid="save-image-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Save Image
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  // Pre-load images as data URLs for html2canvas (R2 cross-origin workaround)
  const preloadImages = async (container) => {
    const imgs = container.querySelectorAll("img[src]");
    const promises = Array.from(imgs).map(async (img) => {
      const src = img.src;
      if (!src || src.startsWith("data:") || src.startsWith("/")) return;
      try {
        // Try direct fetch first
        const resp = await fetch(src, { mode: "cors" });
        const blob = await resp.blob();
        const dataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        img.src = dataUrl;
      } catch {
        try {
          // Fallback: proxy through backend
          const proxyUrl = `${API}/product-images/proxy?url=${encodeURIComponent(src)}`;
          const resp = await fetch(proxyUrl);
          const blob = await resp.blob();
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          img.src = dataUrl;
        } catch {
          // Give up on this image
        }
      }
    });
    await Promise.all(promises);
  };

  const exportAsImage = async (ref, filename) => {
    if (!ref.current) return;
    setExporting(true);
    try {
      await preloadImages(ref.current);
      const canvas = await html2canvas(ref.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Image downloaded!");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportAsPDF = async () => {
    if (!a4Ref.current) return;
    setExporting(true);
    try {
      // Find all page containers inside the a4 wrapper
      const pages = a4Ref.current.querySelectorAll("[data-page]");
      const targets = pages.length > 0 ? Array.from(pages) : [a4Ref.current];

      // Pre-load all images across all pages
      await preloadImages(a4Ref.current);

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < targets.length; i++) {
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(targets[i], {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
        });
        const imgData = canvas.toDataURL("image/png");
        pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
      }

      pdf.save("bosveld-new-arrivals.pdf");
      toast.success("PDF downloaded!");
    } catch (err) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  // Selection View
  if (viewMode === "select") {
    return (
      <>
      <div className="p-4 max-w-7xl mx-auto space-y-4" data-testid="new-arrivals-promoter">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Sparkles className="h-7 w-7 text-amber-500" />
            <div>
              <h1 className="text-2xl font-bold">New Arrivals Promoter</h1>
              <p className="text-sm text-gray-500">
                Select items to promote, then choose a format
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Last</Label>
            <Input
              type="number"
              min="1"
              max="90"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 14)}
              className="w-20"
              data-testid="days-input"
            />
            <Label className="text-sm whitespace-nowrap">days</Label>
            <Button size="sm" variant="outline" onClick={fetchProducts} data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Headline config */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Headline</Label>
                <div className="flex gap-1 flex-wrap mt-1">
                  {HEADLINES.map((h) => (
                    <button
                      key={h}
                      onClick={() => setHeadline(h)}
                      className={`px-2 py-1 text-xs rounded border ${headline === h ? "bg-amber-500 text-white border-amber-500" : "bg-white border-gray-300 hover:bg-gray-50"}`}
                    >
                      {h}
                    </button>
                  ))}
                </div>
                <Input
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  className="mt-1"
                  placeholder="Custom headline"
                  data-testid="headline-input"
                />
              </div>
              <div>
                <Label className="text-xs">Subheadline</Label>
                <Input
                  value={subheadline}
                  onChange={(e) => setSubheadline(e.target.value)}
                  className="mt-1"
                  placeholder="e.g. Now available at great prices"
                  data-testid="subheadline-input"
                />
                <div className="flex items-center gap-2 mt-3">
                  <Checkbox
                    id="showPrices"
                    checked={showPrices}
                    onCheckedChange={setShowPrices}
                    data-testid="show-prices-toggle"
                  />
                  <Label htmlFor="showPrices" className="text-xs cursor-pointer">Show prices on flyer</Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add any product */}
        <div className="relative">
          <Label className="text-xs text-gray-500">Add any product to the flyer</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={addSearch}
              onChange={(e) => handleAddSearch(e.target.value)}
              placeholder="Search by name or PLU code..."
              className="flex-1"
              data-testid="add-product-search"
            />
            {addSearching && <Loader2 className="h-5 w-5 animate-spin text-gray-400 absolute right-3 top-8" />}
          </div>
          {addResults.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
              {addResults.map(p => (
                <button
                  key={p.id}
                  onClick={() => addProductToList(p)}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 flex items-center justify-between border-b last:border-0"
                  data-testid={`add-product-${p.plu_code}`}
                >
                  <div>
                    <span className="font-medium text-sm">{p.name}</span>
                    <span className="text-xs text-gray-400 ml-2">{p.plu_code}</span>
                  </div>
                  <span className="text-xs font-semibold text-amber-600">{formatPrice(p.selling_price_incl_vat)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Selection */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {products.length} product{products.length !== 1 && "s"} found &middot;{" "}
            <span className="font-semibold text-amber-600">{selected.length} selected</span>
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelected(products.map((p) => p.id))}
            >
              Select All
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSelected([])}>
              Deselect All
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {products.map((p) => (
            <div
              key={p.id}
              onClick={() => toggleSelect(p.id)}
              className={`cursor-pointer border-2 rounded-lg p-2 transition-all ${
                selected.includes(p.id)
                  ? "border-amber-500 bg-amber-50 shadow"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`product-select-${p.plu_code}`}
            >
              <div className="aspect-square bg-gray-100 rounded mb-2 flex items-center justify-center overflow-hidden relative group">
                {selected.includes(p.id) && (
                  <div className="absolute top-1 left-1 bg-amber-500 text-white rounded-full p-0.5 z-10">
                    <Check className="h-3 w-3" />
                  </div>
                )}
                {/* Camera button for image upload */}
                <button
                  onClick={(e) => openImageUpload(p, e)}
                  className="absolute top-1 right-1 z-10 bg-white/90 hover:bg-blue-500 hover:text-white text-gray-500 rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-all"
                  data-testid={`upload-image-${p.plu_code}`}
                  title="Add/change image"
                >
                  <Camera className="h-3.5 w-3.5" />
                </button>
                {/* Delete image button */}
                {p.image_url && (
                  <button
                    onClick={(e) => handleDeleteImage(p, e)}
                    className="absolute bottom-1 right-1 z-10 bg-white/90 hover:bg-red-500 hover:text-white text-gray-400 rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-all"
                    title="Remove image"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" />
                ) : (
                  <div className="text-center">
                    <Package className="h-8 w-8 text-gray-300 mx-auto" />
                    <p className="text-[10px] text-gray-400 mt-1">Click camera to add</p>
                  </div>
                )}
              </div>
              <p className="text-xs font-medium line-clamp-2 leading-tight">{p.name}</p>
              <p className="text-sm font-bold text-amber-600 mt-1">
                {formatPrice(p.selling_price_incl_vat)}
              </p>
            </div>
          ))}
        </div>

        {products.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-400">
            <Package className="h-12 w-12 mx-auto mb-3" />
            <p>No new products found in the last {days} days.</p>
            <p className="text-sm">Try increasing the number of days.</p>
          </div>
        )}

        {/* Action Buttons */}
        {selected.length > 0 && (
          <div className="sticky bottom-4 flex gap-3 justify-center">
            <Button
              onClick={() => setViewMode("whatsapp")}
              className="bg-green-600 hover:bg-green-700 shadow-lg"
              data-testid="whatsapp-view-btn"
            >
              <Smartphone className="h-4 w-4 mr-2" />
              WhatsApp Image ({selected.length})
            </Button>
            <Button
              onClick={() => setViewMode("a4")}
              className="bg-blue-600 hover:bg-blue-700 shadow-lg"
              data-testid="a4-view-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              A4 Flyer ({selected.length})
            </Button>
          </div>
        )}
      </div>
      {renderUploadDialog()}
      </>
    );
  }

  // WhatsApp Screenshot View
  if (viewMode === "whatsapp") {
    return (
      <div className="p-4 max-w-2xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setViewMode("select")} data-testid="back-btn">
            Back to Selection
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={() => exportAsImage(promoRef, "bosveld-new-arrivals-whatsapp")}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="download-whatsapp-btn"
            >
              {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Download Image
            </Button>
            <Button
              onClick={() => {
                const printWindow = window.open('', '_blank');
                if (!printWindow) { toast.error("Pop-up blocked"); return; }
                const content = promoRef.current?.outerHTML || '';
                printWindow.document.write(`<!DOCTYPE html><html><head><style>body{margin:0;display:flex;justify-content:center;}@media print{body{margin:0;}}</style></head><body>${content}</body></html>`);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
              }}
              variant="outline"
              data-testid="print-whatsapp-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500 text-center">
          Screenshot or download this image and share it on WhatsApp
        </p>

        {/* WhatsApp Promo Card */}
        <div ref={promoRef} className="bg-white rounded-xl overflow-hidden shadow-xl" style={{ maxWidth: 540 }}>
          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #1e3a5f 0%, #0c2340 100%)",
              padding: "24px 20px 16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img
                src="/logo.jpg"
                alt="BBH"
                style={{ height: 48, borderRadius: 8, background: "white", padding: 3 }}

              />
              <div>
                <div style={{ color: "white", fontWeight: 800, fontSize: 16, letterSpacing: 0.5 }}>
                  BOSVELD BOU & HARDWARE
                </div>
                <div style={{ color: "#93c5fd", fontSize: 11 }}>
                  083 278 5937 &middot; Tom Burke
                </div>
              </div>
            </div>
          </div>

          {/* Headline Bar */}
          <div
            style={{
              background: "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)",
              padding: "10px 20px",
              textAlign: "center",
            }}
          >
            <div style={{ color: "white", fontWeight: 900, fontSize: 22, letterSpacing: 1 }}>
              {headline}
            </div>
            {subheadline && (
              <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 11, marginTop: 2 }}>
                {subheadline}
              </div>
            )}
          </div>

          {/* Products Grid */}
          <div
            style={{
              padding: 16,
              display: "grid",
              gridTemplateColumns: selectedProducts.length <= 2 ? "1fr" : "1fr 1fr",
              gap: 12,
            }}
          >
            {selectedProducts.slice(0, 8).map((p) => (
              <div
                key={p.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  overflow: "hidden",
                  background: "#fafafa",
                }}
              >
                <div
                  style={{
                    aspectRatio: "4/3",
                    background: "#f3f4f6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      background: "#f59e0b",
                      color: "white",
                      fontSize: 9,
                      fontWeight: 800,
                      padding: "2px 8px",
                      borderRadius: 4,
                    }}
                  >
                    NEW
                  </div>
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    />
                  ) : (
                    <div style={{ color: "#d1d5db", fontSize: 32 }}>&#128230;</div>
                  )}
                </div>
                <div style={{ padding: "8px 10px" }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                      lineHeight: 1.3,
                      minHeight: 32,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {p.name}
                  </div>
                  {showPrices && (
                    <>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#d97706", marginTop: 4 }}>
                        {formatPrice(p.selling_price_incl_vat)}
                      </div>
                      <div style={{ fontSize: 9, color: "#9ca3af" }}>Incl. VAT</div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div
            style={{
              background: "#1e3a5f",
              padding: "10px 20px",
              textAlign: "center",
              color: "white",
              fontSize: 11,
            }}
          >
            <div style={{ fontWeight: 700 }}>
              Visit us: R572, Farm Van Wyksfontein, Tom Burke
            </div>
            <div style={{ color: "#93c5fd", marginTop: 2 }}>
              Mon-Sat 07:30-18:00 &middot; 083 278 5937
            </div>
          </div>
        </div>
      </div>
    );
  }

  // A4 Print View
  if (viewMode === "a4") {
    // Split products into pages of 12
    const ITEMS_PER_PAGE = 12;
    const pages = [];
    for (let i = 0; i < selectedProducts.length; i += ITEMS_PER_PAGE) {
      pages.push(selectedProducts.slice(i, i + ITEMS_PER_PAGE));
    }

    const renderA4Page = (pageProducts, pageIndex, isLast) => (
      <div
        key={pageIndex}
        data-page={pageIndex}
        style={{
          width: 794,
          height: 1123,
          margin: "0 auto",
          background: "white",
          fontFamily: "Arial, Helvetica, sans-serif",
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e3a5f 0%, #0c2340 100%)",
            padding: pageIndex === 0 ? "28px 36px 20px" : "14px 36px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <img
              src="/logo.jpg"
              alt="BBH"
              style={{ height: pageIndex === 0 ? 60 : 40, borderRadius: 10, background: "white", padding: 4 }}
            />
            <div>
              <div style={{ color: "white", fontWeight: 900, fontSize: pageIndex === 0 ? 22 : 16, letterSpacing: 1 }}>
                BOSVELD BOU & HARDWARE
              </div>
              {pageIndex === 0 && (
                <div style={{ color: "#93c5fd", fontSize: 12, marginTop: 2 }}>
                  Your Trusted Hardware Partner &middot; Tom Burke
                </div>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", color: "#93c5fd", fontSize: 12 }}>
            <div>083 278 5937</div>
            <div>bosveldbh@gmail.com</div>
          </div>
        </div>

        {/* Headline (first page only) */}
        {pageIndex === 0 && (
          <div
            style={{
              background: "linear-gradient(90deg, #f59e0b 0%, #d97706 100%)",
              padding: "14px 36px",
              textAlign: "center",
            }}
          >
            <div style={{ color: "white", fontWeight: 900, fontSize: 30, letterSpacing: 2 }}>
              {headline}
            </div>
            {subheadline && (
              <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, marginTop: 2 }}>
                {subheadline}
              </div>
            )}
          </div>
        )}
        {pageIndex > 0 && (
          <div style={{ background: "#f59e0b", padding: "6px 36px", textAlign: "center" }}>
            <div style={{ color: "white", fontWeight: 800, fontSize: 14 }}>
              {headline} (continued)
            </div>
          </div>
        )}

        {/* Products */}
        <div
          style={{
            padding: "20px 36px",
            display: "grid",
            gridTemplateColumns: pageProducts.length <= 3 ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
            gap: 14,
            flex: 1,
            alignContent: "start",
          }}
        >
          {pageProducts.map((p) => {
            const cols = pageProducts.length <= 3 ? 3 : 4;
            const imgHeight = cols === 4 ? 150 : 200;
            // Allow up to 3 lines for the description on A4
            const maxNameChars = cols === 4 ? 70 : 110;
            const displayName = (p.name || "").length > maxNameChars
              ? (p.name || "").slice(0, maxNameChars - 1).trimEnd() + "…"
              : p.name || "";
            return (
            <div
              key={p.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                overflow: "hidden",
                background: "#fafafa",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: imgHeight,
                  background: "#f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 6,
                    left: 6,
                    background: "#f59e0b",
                    color: "white",
                    fontSize: 10,
                    fontWeight: 800,
                    padding: "2px 10px",
                    borderRadius: 4,
                    zIndex: 2,
                  }}
                >
                  NEW
                </div>
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt=""
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      display: "block",
                    }}
                  />
                ) : (
                  <div style={{ color: "#d1d5db", fontSize: 40 }}>&#128230;</div>
                )}
              </div>
              <div style={{ padding: "8px 10px" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#374151",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                >
                  {displayName}
                </div>
                {showPrices && (
                  <>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#d97706", marginTop: 4 }}>
                      {formatPrice(p.selling_price_incl_vat)}
                    </div>
                    <div style={{ fontSize: 9, color: "#9ca3af" }}>Incl. VAT</div>
                  </>
                )}
              </div>
            </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#1e3a5f",
            padding: "14px 36px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "auto",
          }}
        >
          <div style={{ color: "white", fontSize: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Visit Us Today!</div>
            <div style={{ color: "#93c5fd", marginTop: 2 }}>
              R572, Farm Van Wyksfontein, Tom Burke
            </div>
          </div>
          <div style={{ textAlign: "right", color: "#93c5fd", fontSize: 12 }}>
            {pages.length > 1 && <div>Page {pageIndex + 1} of {pages.length}</div>}
            <div>Mon-Sat 07:30-18:00</div>
            <div>083 278 5937</div>
          </div>
        </div>
      </div>
    );

    return (
      <div className="p-4 max-w-4xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setViewMode("select")} data-testid="back-btn-a4">
            Back to Selection
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={() => exportAsImage(a4Ref, "bosveld-new-arrivals-a4")}
              disabled={exporting}
              variant="outline"
              data-testid="download-a4-image-btn"
            >
              {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Image className="h-4 w-4 mr-2" />}
              Download Image
            </Button>
            <Button
              onClick={exportAsPDF}
              disabled={exporting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="download-pdf-btn"
            >
              {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Download PDF
            </Button>
            <Button
              onClick={() => window.print()}
              variant="outline"
              data-testid="print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        </div>

        {/* A4 Pages */}
        <div ref={a4Ref} className="space-y-6">
          {pages.map((pageProducts, idx) => renderA4Page(pageProducts, idx, idx === pages.length - 1))}
        </div>
      </div>
    );
  }

  return null;
};

export default NewArrivalsPromoter;
